import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { VALIDATORS } from 'app/constants';
import { AppCustomLine as CustomLine, AppLooseArticle as LooseArticle } from 'graphql/models/extended';
import { DataTableResourceType } from 'models/data.model';
import { OfferBase } from 'models/offer.model';
import { SvgIconSizeMod } from 'models/layout-mod.model';
import { Template, TemplateType } from 'models/template.model';
import { BaseComponent } from 'shared/base/base.component';
import { CoreDataService } from 'shared/services/core-data.service';
import { FormService } from 'shared/services/form.service';
import { StoreService } from 'shared/services/store.service';
import { LoadingService } from 'app/loading.service';

@Component({
  selector: 'app-add-loose-article-to-offer',
  templateUrl: './add-loose-article-to-offer.component.html',
  styleUrls: ['./add-loose-article-to-offer.component.scss']
})
export class AddLooseArticleToOfferComponent extends BaseComponent implements OnInit {
  @Input() data!: string;

  protected readonly svgIconSizeMod = SvgIconSizeMod;

  readonly resourceType: DataTableResourceType = 'LooseArticle';

  dropdownData: Record<string, any> = {
    positionTemplates: []
  };

  isCollapsed = true;

  selectedLooseArticle!: LooseArticle;

  form!: UntypedFormGroup;

  formReady = false;

  position!: CustomLine;

  offer!: OfferBase;

  maxDescriptionLength = VALIDATORS.MAX_LENGTH.LV_POSITION_TEXT;

  constructor(
    protected fb: UntypedFormBuilder,
    private formService: FormService,
    private coreDataService: CoreDataService,
    private storeService: StoreService,
    private loadingService: LoadingService
  ) {
    super();
  }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    combineLatest([
      this.storeService.getCurrentOffer(),
      this.storeService.getCustomPosition()
    ])
      .pipe(take(1))
      .subscribe(([offer, position]) => {
        this.offer = offer;
        this.position = position;

        this.setupPositionForm();
        this.getPositionTemplates();
      });
  }

  private setupPositionForm(): void {
    this.storeService.setFormReady(this.formReady);
    this.form = this.formService.getPositionBaseForm(this.position);
    this.form.addControl('positionTemplate', this.fb.control(null));
    this.form.addControl('text', this.fb.control('', Validators.maxLength(VALIDATORS.MAX_LENGTH.TEXTAREA)));
    this.form.addControl('selectedArticle', this.fb.control(this.selectedLooseArticle, Validators.required));

    this.loadingService.isLoading$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isLoading: boolean): void => {
        if (isLoading) {
          this.form.disable({ emitEvent: false });
        } else {
          this.form.enable({ emitEvent: false });
        }
      });

    this.formService.detectPositionQuantityFormChanges(this.form, this.destroyed$);

    this.formReady = true;
    this.storeService.setFormReady(this.formReady);

    this.form.controls.positionTemplate.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        this.form.controls.text.setValue(value, { emitEvent: false });
      });
  }

  private getPositionTemplates(): void {
    this.coreDataService.getTemplates(100, TemplateType.POSITION)
      .subscribe((templates: Template[]) => {
        this.dropdownData.positionTemplates = templates;
      });
  }

  onItemSelected(item: LooseArticle): void {
    if (item) {
      this.selectedLooseArticle = item;
      this.form.controls.selectedArticle.setValue(this.selectedLooseArticle);
    }
  }
}
